import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ITenant } from '../interfaces/tenant/ITenant';
import { IIntegration } from '../interfaces/IIntegration';
import { PricingPlanTypes } from '../enums/billing/PricingPlanTypes.enum';
import {SentryService} from 'app/common/services/sentry.service';
import {SessionService} from 'app/common/services/session.service';

@Injectable({
  providedIn: 'root'
})
export class ChatManagerService {

  constructor(@Inject(DOCUMENT) private document: Document,
              private sentryService: SentryService,
              private sessionService: SessionService,
  ) { }

  setChatAttributes(name: string, email: string, tenant: ITenant, integration: IIntegration) {
    // Delay loading Intercom just as in your example
    setTimeout(() => {
      this.loadIntercom(name, email, tenant, integration);
      this.loadHotjar(name, email, tenant, integration);
      this.sentryService.setUserContext(this.sessionService.currentUser, tenant, integration);
    }, 3000);
  }

  openChat() {
    this.getWindow().Intercom('show');
  }

  loadIntercom(name: string, email: string, tenant: ITenant, integration: IIntegration) {
    const bootParams: any = {
      api_base: 'https://api-iam.intercom.io',
      app_id: 'dsra2smq',
      name,
      email,
      created_at: new Date().getTime(),
      company: {},
      custom_attributes: {}
    };

    let notes = [];

    if (tenant) {
      const pricingPlanName = tenant.pricingPlan != null
        ? PricingPlanTypes[tenant.pricingPlan]
        : 'default-plan';

      if (tenant.name) {
        bootParams.company = {
          name: tenant.name,
          plan: pricingPlanName ?? 'Unknown',
          company_id: tenant.id ?? tenant.name
        };

        notes.push(`Tenant Name: ${tenant.name}`);
        notes.push(`Pricing Plan: ${pricingPlanName}`);
      }
    }

    if (integration) {
      if (integration.shopUrl) {
        bootParams.company.website = integration.shopUrl;
        bootParams.company.shopUrl = integration.shopUrl;
        notes.push(`Shop URL: ${integration.shopUrl}`);
      }

      if (integration.productsCount) {
        bootParams.company.size = integration.productsCount;
        bootParams.company.productsCount = integration.productsCount;
        notes.push(`Products Count: ${integration.productsCount}`);
      }

      if (integration.shopCountry) {
        bootParams.company.country = integration.shopCountry;
        notes.push(`Shop Country: ${integration.shopCountry}`);
      }
    }

    if (notes.length > 0) {
      const concatenatedNotes = notes.join('; ');
      bootParams.company.notes = concatenatedNotes;
      bootParams.notes = concatenatedNotes; // or custom_attributes
    }

    this.getWindow().Intercom('boot', bootParams);
  }

  /**
   * Load or set Hotjar attributes in a similar style to Intercom.
   * If you have your Hotjar script in index.html, you can just
   * call `hj('identify', ...)`.
   */
  loadHotjar(name: string, email: string, tenant: ITenant, integration: IIntegration) {
    try {


      const hjInstance = this.getWindow().hj;

      // If the Hotjar script is loaded, `hj` should exist
      if (!hjInstance) {
        console.warn("Hotjar (hj) not found in the window. Make sure it's loaded in index.html.");
        return;
      }

      // Build up notes from tenant/integration to pass as user properties
      let notes = [];

      if (tenant) {
        const pricingPlanName = tenant.pricingPlan != null
          ? PricingPlanTypes[tenant.pricingPlan]
          : 'default-plan';

        if (tenant.name) {
          notes.push(`Tenant Name: ${tenant.name}`);
          notes.push(`Pricing Plan: ${pricingPlanName}`);
        }
      }

      if (integration) {
        if (integration.shopUrl) {
          notes.push(`Shop URL: ${integration.shopUrl}`);
        }
        if (integration.productsCount) {
          notes.push(`Products Count: ${integration.productsCount}`);
        }
        if (integration.shopCountry) {
          notes.push(`Shop Country: ${integration.shopCountry}`);
        }
      }

      // Concatenate all notes
      const concatenatedNotes = notes.join('; ') || '';

      /**
       * Normally you'd use some internal user ID or email as a unique ID for Hotjar.
       * For example, many sites do:
       *   hj('identify', user.id, { ... user props ... });
       */
      const uniqueUserId = email || name || 'anonymous_user';

      hjInstance('identify', uniqueUserId, {
        name: name,
        email: email,
        notes: concatenatedNotes
      });
    }
    catch (e) {
      console.log("hotjar setting loading issue", e);
    }

  }

  getWindow(): any {
    // If you want direct access to window, you can also do:
    // return window;
    // But you appear to be using `document.defaultView?.window` in your code
    return this.document?.defaultView?.window;
  }
}
